import {
  AttributeHeader,
  AttributeValue,
  Button,
  Card,
  Icon,
  PhoneNumberDisplay,
  createCssVariableString,
} from "best-common-react";
import React, { useState, useMemo } from "react";
import styled from "styled-components";
import CreditCardMappings from "../../constants/CreditCardMappings";
import useCyberSource, { getCCExpInfo } from "../../util/CreditCardUtil";
import { SubdivisionAbbreviation } from "../../util/SubdivisionUtil";
import DeleteCreditCardModal from "./DeleteCreditCardModal";

const CardHolder = styled.div.attrs(() => ({
  className: "bcr-me-2",
}))`
  width: 32.5rem;
  color: ${createCssVariableString("color")};
`;

const AddressValue = styled(AttributeValue)`
  &&& {
    margin: 0;
    margin-bottom: 0 !important;
  }
`;

const EndingIn = styled.div`
  font-size: 1rem;
  color: ${createCssVariableString("color")};
`;

const Expiration = styled.div`
  color: ${(props) => props.color};
  font-size: 1rem;
`;

const SmallerAttributeHeader = styled(AttributeHeader)`
  &&& {
    font-size: 0.875rem;
  }
`;

const DefaultPaymentMethod = styled.b`
  font-size: 0.875rem;
  color: ${createCssVariableString("color")};
`;

export const CreditCardIcon = ({ type, size = 2, ...rest }) => (
  <i className={`fab ${type}`} style={{ fontSize: `${size}rem` }} {...rest} />
);

const CreditCardInfo = ({
  firstName,
  lastName,
  addressLine1,
  addressLine2,
  city,
  state,
  zip,
  phone,
  countryName,
  countryId,
  isDefault,
  onMakeDefault,
}) => (
  <div className="d-flex bcr-p-1">
    <div className="flex-grow-1">
      <SmallerAttributeHeader>Billing Address</SmallerAttributeHeader>
      <AddressValue>
        {firstName} {lastName}
      </AddressValue>
      <AddressValue>{addressLine1}</AddressValue>
      {addressLine2 && <AddressValue>{addressLine2}</AddressValue>}
      <AddressValue>
        {city}, <SubdivisionAbbreviation name={state} countryId={countryId} /> {zip}
      </AddressValue>
      <AddressValue>{countryName}</AddressValue>
      {!isDefault && (
        <Button variant="default" className="bcr-mt-3" onClick={onMakeDefault}>
          Make Default
        </Button>
      )}
    </div>
    <div>
      <SmallerAttributeHeader>Phone</SmallerAttributeHeader>
      <AttributeValue>
        <PhoneNumberDisplay phoneNumber={phone} id={phone} />
      </AttributeValue>
    </div>
  </div>
);

const CreditCard = ({ userId, refresh, ...cc }) => {
  const { creditCard, onCancel, onDelete, makeDefault } = useCyberSource(cc, userId);
  const [showDeleteModal, setShowDeleteModal] = React.useState(false);
  const [loading, setLoading] = useState(false);

  const ccExpInfo = useMemo(() => getCCExpInfo(creditCard.ccExp), [creditCard.ccExp]);

  const deleteCC = () => {
    setLoading(true);
    onDelete()
      .then(() => {
        setShowDeleteModal(false);
        refresh();
        setLoading(false);
      })
      .catch(() => {
        setLoading(false);
      });
  };

  const onClose = () => {
    onCancel();
    setShowDeleteModal(false);
  };

  const onMakeDefault = () => {
    makeDefault().then(() => {
      refresh();
    });
  };

  return (
    <>
      <DeleteCreditCardModal
        show={showDeleteModal}
        onClose={onClose}
        onDelete={deleteCC}
        loading={loading}
        {...creditCard}
      />
      <CardHolder>
        <div>
          {creditCard.isDefault ? (
            <DefaultPaymentMethod>Default Payment Method</DefaultPaymentMethod>
          ) : (
            <span>&nbsp;</span>
          )}
        </div>
        <Card>
          <Card.Header>
            <div className="d-flex bcr-p-1">
              <div className="d-flex align-items-center">
                <CreditCardIcon type={creditCard.ccType ? CreditCardMappings[creditCard.ccType.ccTypeName!] : ""} />
              </div>
              <div className="bcr-ms-3 flex-grow-1">
                <EndingIn>
                  {creditCard.ccNickname} ending in {creditCard.ccLast4}
                </EndingIn>
                <Expiration color={ccExpInfo.color}>
                  {ccExpInfo.text} {creditCard.ccExp}
                </Expiration>
              </div>
              <div className="d-flex align-items-center">
                <Icon iconName="fa-trash-alt" onClick={() => setShowDeleteModal(true)} />
              </div>
            </div>
          </Card.Header>
          <Card.Body>
            <CreditCardInfo {...creditCard} onMakeDefault={onMakeDefault} />
          </Card.Body>
        </Card>
      </CardHolder>
    </>
  );
};

export default CreditCard;
